<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const campaignId = computed(() => route.params.campaignId);
const selectedExecutionId = ref(null);

const uiFlags = computed(() => store.getters['campaignsV2/getUIFlags']);
const analytics = computed(
  () => store.getters['campaignsV2/getAnalytics'](campaignId.value) || {}
);
const executions = computed(() => analytics.value.executions || []);
const summary = computed(() => analytics.value.summary || {});

const executionMessages = computed(() => {
  if (!selectedExecutionId.value) return [];
  return (
    store.getters['campaignsV2/getExecutionMessages'](
      selectedExecutionId.value
    ) || []
  );
});

const selectedExecution = computed(() =>
  executions.value.find(e => e.id === selectedExecutionId.value)
);

const goBack = () => {
  if (selectedExecutionId.value) {
    selectedExecutionId.value = null;
    return;
  }
  router.push({
    name: 'campaigns_index',
    params: route.params,
  });
};

const viewExecution = execution => {
  selectedExecutionId.value = execution.id;
  store.dispatch('campaignsV2/fetchExecutionMessages', {
    campaignId: campaignId.value,
    executionId: execution.id,
    page: 1,
  });
};

const exportCampaign = async () => {
  try {
    await store.dispatch('campaignsV2/exportCampaign', campaignId.value);
    useAlert(t('CAMPAIGN.V2.ANALYTICS.EXPORT_SUCCESS'));
  } catch {
    useAlert(t('CAMPAIGN.V2.ANALYTICS.EXPORT_ERROR'));
  }
};

const statusClass = status => {
  const classes = {
    sent: 'bg-n-blue-3 text-n-blue-11',
    delivered: 'bg-n-teal-3 text-n-teal-11',
    read: 'bg-n-green-3 text-n-green-11',
    failed: 'bg-n-red-3 text-n-red-11',
    pending: 'bg-n-amber-3 text-n-amber-11',
    processing: 'bg-n-amber-3 text-n-amber-11',
    completed: 'bg-n-teal-3 text-n-teal-11',
  };
  return classes[status] || 'bg-n-alpha-3 text-n-slate-11';
};

const formatDate = dateStr => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

onMounted(() => {
  store.dispatch('campaignsV2/fetchAnalytics', campaignId.value);
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full overflow-auto bg-n-surface-1">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak">
      <div class="flex items-center gap-4">
        <button
          class="text-sm text-n-slate-11 hover:text-n-slate-12"
          @click="goBack"
        >
          &larr;
          {{
            selectedExecutionId
              ? t('CAMPAIGN.V2.ANALYTICS.MESSAGES.BACK')
              : t('CAMPAIGN.V2.ANALYTICS.BACK')
          }}
        </button>
        <h1 class="text-lg font-medium text-n-slate-12">
          {{ t('CAMPAIGN.V2.ANALYTICS.TITLE') }}
        </h1>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
          @click="exportCampaign"
        >
          {{ t('CAMPAIGN.V2.ANALYTICS.EXPORT') }}
        </button>
      </div>
    </div>

    <div v-if="uiFlags.isFetchingAnalytics" class="flex items-center justify-center py-10">
      <Spinner />
    </div>

    <div v-else-if="!selectedExecutionId" class="flex-1 px-6 py-6 space-y-8">
      <!-- Summary Cards -->
      <div class="grid grid-cols-5 gap-4">
        <div class="p-4 rounded-lg border border-n-weak">
          <div class="text-2xl font-bold text-n-slate-12">
            {{ summary.total_sent || 0 }}
          </div>
          <div class="text-xs text-n-slate-11 mt-1">
            {{ t('CAMPAIGN.V2.ANALYTICS.SUMMARY.SENT') }}
          </div>
        </div>
        <div class="p-4 rounded-lg border border-n-weak">
          <div class="text-2xl font-bold text-n-teal-11">
            {{ summary.total_delivered || 0 }}
          </div>
          <div class="text-xs text-n-slate-11 mt-1">
            {{ t('CAMPAIGN.V2.ANALYTICS.SUMMARY.DELIVERED') }}
          </div>
        </div>
        <div class="p-4 rounded-lg border border-n-weak">
          <div class="text-2xl font-bold text-n-green-11">
            {{ summary.total_read || 0 }}
          </div>
          <div class="text-xs text-n-slate-11 mt-1">
            {{ t('CAMPAIGN.V2.ANALYTICS.SUMMARY.READ') }}
          </div>
        </div>
        <div class="p-4 rounded-lg border border-n-weak">
          <div class="text-2xl font-bold text-n-red-11">
            {{ summary.total_failed || 0 }}
          </div>
          <div class="text-xs text-n-slate-11 mt-1">
            {{ t('CAMPAIGN.V2.ANALYTICS.SUMMARY.FAILED') }}
          </div>
        </div>
        <div class="p-4 rounded-lg border border-n-weak">
          <div class="text-2xl font-bold text-n-blue-11">
            {{ summary.total_interactions || 0 }}
          </div>
          <div class="text-xs text-n-slate-11 mt-1">
            {{ t('CAMPAIGN.V2.ANALYTICS.SUMMARY.INTERACTIONS') }}
          </div>
        </div>
      </div>

      <!-- Executions Table -->
      <div>
        <h2 class="text-base font-medium text-n-slate-12 mb-4">
          {{ t('CAMPAIGN.V2.ANALYTICS.EXECUTIONS.TITLE') }}
        </h2>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-n-weak">
                <th class="px-3 py-3 text-left font-medium text-n-slate-11">
                  {{ t('CAMPAIGN.V2.ANALYTICS.EXECUTIONS.NUMBER') }}
                </th>
                <th class="px-3 py-3 text-left font-medium text-n-slate-11">
                  {{ t('CAMPAIGN.V2.ANALYTICS.EXECUTIONS.STATUS') }}
                </th>
                <th class="px-3 py-3 text-left font-medium text-n-slate-11">
                  {{ t('CAMPAIGN.V2.ANALYTICS.EXECUTIONS.SCHEDULED_AT') }}
                </th>
                <th class="px-3 py-3 text-right font-medium text-n-slate-11">
                  {{ t('CAMPAIGN.V2.ANALYTICS.EXECUTIONS.TOTAL_CONTACTS') }}
                </th>
                <th class="px-3 py-3 text-right font-medium text-n-slate-11">
                  {{ t('CAMPAIGN.V2.ANALYTICS.EXECUTIONS.SENT') }}
                </th>
                <th class="px-3 py-3 text-right font-medium text-n-slate-11">
                  {{ t('CAMPAIGN.V2.ANALYTICS.EXECUTIONS.DELIVERED') }}
                </th>
                <th class="px-3 py-3 text-right font-medium text-n-slate-11">
                  {{ t('CAMPAIGN.V2.ANALYTICS.EXECUTIONS.READ') }}
                </th>
                <th class="px-3 py-3 text-right font-medium text-n-slate-11">
                  {{ t('CAMPAIGN.V2.ANALYTICS.EXECUTIONS.FAILED') }}
                </th>
                <th class="px-3 py-3 text-right font-medium text-n-slate-11">
                  {{ t('CAMPAIGN.V2.ANALYTICS.EXECUTIONS.INTERACTIONS') }}
                </th>
                <th class="px-3 py-3 text-right font-medium text-n-slate-11" />
              </tr>
            </thead>
            <tbody>
              <template
                v-for="execution in executions"
                :key="execution.id"
              >
                <tr class="border-b border-n-weak hover:bg-n-alpha-1">
                  <td class="px-3 py-3 font-medium text-n-slate-12">
                    #{{ execution.execution_number }}
                  </td>
                  <td class="px-3 py-3">
                    <span
                      class="px-2 py-1 text-xs font-medium rounded-full"
                      :class="statusClass(execution.status)"
                    >
                      {{ execution.status }}
                    </span>
                  </td>
                  <td class="px-3 py-3 text-n-slate-11">
                    {{ formatDate(execution.scheduled_at) }}
                  </td>
                  <td class="px-3 py-3 text-right text-n-slate-11">
                    {{ execution.total_contacts }}
                  </td>
                  <td class="px-3 py-3 text-right text-n-slate-11">
                    {{ execution.sent_count }}
                  </td>
                  <td class="px-3 py-3 text-right text-n-teal-11">
                    {{ execution.delivered_count }}
                  </td>
                  <td class="px-3 py-3 text-right text-n-green-11">
                    {{ execution.read_count }}
                  </td>
                  <td class="px-3 py-3 text-right text-n-red-11">
                    {{ execution.failed_count }}
                  </td>
                  <td class="px-3 py-3 text-right text-n-blue-11">
                    {{ execution.interaction_count }}
                  </td>
                  <td class="px-3 py-3 text-right">
                    <button
                      v-if="execution.total_contacts > 0"
                      class="text-xs font-medium text-n-brand hover:underline"
                      @click="viewExecution(execution)"
                    >
                      {{ t('CAMPAIGN.V2.ANALYTICS.EXECUTIONS.VIEW_DETAILS') }}
                    </button>
                  </td>
                </tr>
                <tr
                  v-if="execution.error_summary && execution.error_summary.error"
                  class="border-b border-n-weak bg-n-red-2"
                >
                  <td
                    colspan="10"
                    class="px-3 py-2 text-xs text-n-red-11"
                  >
                    <span class="font-medium">{{ t('CAMPAIGN.V2.ANALYTICS.MESSAGES.ERROR') }}:</span>
                    {{ execution.error_summary.error }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Execution Messages Detail -->
    <div v-else class="flex-1 px-6 py-6">
      <h2 class="text-base font-medium text-n-slate-12 mb-4">
        {{
          t('CAMPAIGN.V2.ANALYTICS.MESSAGES.TITLE', {
            number: selectedExecution?.execution_number,
          })
        }}
      </h2>

      <div
        v-if="uiFlags.isFetchingMessages"
        class="flex items-center justify-center py-10"
      >
        <Spinner />
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak">
              <th class="px-3 py-3 text-left font-medium text-n-slate-11">
                {{ t('CAMPAIGN.V2.ANALYTICS.MESSAGES.CONTACT_NAME') }}
              </th>
              <th class="px-3 py-3 text-left font-medium text-n-slate-11">
                {{ t('CAMPAIGN.V2.ANALYTICS.MESSAGES.PHONE') }}
              </th>
              <th class="px-3 py-3 text-left font-medium text-n-slate-11">
                {{ t('CAMPAIGN.V2.ANALYTICS.MESSAGES.EMAIL') }}
              </th>
              <th class="px-3 py-3 text-left font-medium text-n-slate-11">
                {{ t('CAMPAIGN.V2.ANALYTICS.MESSAGES.STATUS') }}
              </th>
              <th class="px-3 py-3 text-left font-medium text-n-slate-11">
                {{ t('CAMPAIGN.V2.ANALYTICS.MESSAGES.ERROR') }}
              </th>
              <th class="px-3 py-3 text-left font-medium text-n-slate-11">
                {{ t('CAMPAIGN.V2.ANALYTICS.MESSAGES.PROCESSED_AT') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="msg in executionMessages"
              :key="msg.id"
              class="border-b border-n-weak hover:bg-n-alpha-1"
            >
              <td class="px-3 py-3 text-n-slate-12">
                {{ msg.contact?.name || '-' }}
              </td>
              <td class="px-3 py-3 text-n-slate-11">
                {{ msg.contact?.phone_number || '-' }}
              </td>
              <td class="px-3 py-3 text-n-slate-11">
                {{ msg.contact?.email || '-' }}
              </td>
              <td class="px-3 py-3">
                <span
                  class="px-2 py-1 text-xs font-medium rounded-full"
                  :class="statusClass(msg.status)"
                >
                  {{ msg.status }}
                </span>
              </td>
              <td class="px-3 py-3 text-xs text-n-red-11">
                {{ msg.error_message || '-' }}
              </td>
              <td class="px-3 py-3 text-n-slate-11">
                {{ formatDate(msg.processed_at) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
